import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios'

// API Configuration - uses environment variables for production/development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v2'
const DEFAULT_TIMEOUT = 30000

// =============================================================================
// SUBSCRIPTION EVENT EMITTER
// =============================================================================

type EventHandler = (...args: unknown[]) => void

class SubscriptionEventEmitter {
  private handlers: Record<string, EventHandler[]> = {}

  on(event: string, handler: EventHandler) {
    if (!this.handlers[event]) this.handlers[event] = []
    this.handlers[event].push(handler)
  }

  off(event: string, handler: EventHandler) {
    if (!this.handlers[event]) return
    this.handlers[event] = this.handlers[event].filter(h => h !== handler)
  }

  emit(event: string, ...args: unknown[]) {
    if (!this.handlers[event]) return
    this.handlers[event].forEach(h => h(...args))
  }
}

export const subscriptionEvents = new SubscriptionEventEmitter()

// =============================================================================
// TOKEN REFRESH MUTEX
// =============================================================================

let isRefreshing = false
let refreshPromise: Promise<string | null> | null = null

async function refreshAuthToken(): Promise<string | null> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      // 2026-05-23: backend `/auth/refresh` (auth_v2) requires the refresh
      // token in the body, not the Authorization header. AuthContext now
      // persists the refresh token at login; we read it from localStorage
      // and send it in the body. Pre-fix this POSTed `{}` and got 422
      // every poll once the access token neared expiry.
      const refreshToken = localStorage.getItem('refresh_token')
      if (!refreshToken) return null

      const response = await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        { refresh_token: refreshToken },
      )

      if (response.data?.access_token) {
        localStorage.setItem('auth_token', response.data.access_token)
        return response.data.access_token
      }
      return null
    } catch {
      console.warn('Token refresh failed')
      return null
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// =============================================================================
// AXIOS INSTANCE
// =============================================================================

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Endpoints that are safe to call without a token (public/health/auth).
// For everything else we short-circuit to a 401 client-side when no token
// is present, which prevents the "before-login request storm" observed
// on Spoke: the shell components (alerts banner, fleet grid, brain KPIs)
// all fire fetches at first paint, each one getting 401 and logging an
// error toast before the user is even shown the login form.
const AUTH_FREE_PREFIXES = [
  '/auth/',
  '/health',
  '/public/',
  '/deployment-mode',
  '/docs',
  '/openapi.json',
  '/diagnostics',
]

function isAuthFree(url?: string): boolean {
  if (!url) return false
  return AUTH_FREE_PREFIXES.some((p) => url.includes(p))
}

// Request interceptor for auth tokens, industry context, and token refresh
apiClient.interceptors.request.use(
  async (config) => {
    // Add auth token
    let token = localStorage.getItem('auth_token')

    // Before login: skip protected endpoints entirely so the UI doesn't
    // pelt the server with 401-bound requests. A `CanceledError` here
    // propagates to the caller; the caller's own `.catch` will quietly
    // drop the failure (matches the existing ?.catch(() => null) pattern
    // used by most widgets).
    if (!token && !isAuthFree(config.url)) {
      const controller = new AbortController()
      controller.abort()
      config.signal = controller.signal
      // Surface the unauthenticated state once per page load so the user
      // sees an amber "Sign in" banner instead of silent (Cached) /
      // [SIMULATED] labels that look like the platform is broken.
      // (May 4 2026, Tier-R++ revision.)
      try {
        const w = window as unknown as { _authMissingNotified?: boolean }
        if (!w._authMissingNotified) {
          w._authMissingNotified = true
          window.dispatchEvent(new CustomEvent('ryedore:session-expired', {
            detail: { reason: 'no auth token', endpoint: config.url },
          }))
        }
      } catch { /* SSR */ }
    }

    // Proactive token refresh: if token expires in < 5 minutes, refresh it
    if (token && !config.url?.includes('/auth/refresh')) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]))
        const expiresIn = payload.exp * 1000 - Date.now()

        if (expiresIn < 5 * 60 * 1000 && expiresIn > 0) {
          const newToken = await refreshAuthToken()
          if (newToken) {
            token = newToken
          }
        }
      } catch {
        // Token decode failed — use as-is
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add industry ID (critical for backend to return correct data)
    const industryId = localStorage.getItem('ryedore-industry')
    if (industryId) {
      config.headers['X-Industry-ID'] = industryId
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor for error handling, token tracking, and grace period
apiClient.interceptors.response.use(
  (response) => {
    // Extract token usage headers from successful responses
    const tokensUsed = response.headers['x-tokens-used']
    const tokensLimit = response.headers['x-tokens-limit']
    const tokensRemaining = response.headers['x-tokens-remaining']

    if (tokensUsed && tokensLimit) {
      subscriptionEvents.emit('tokenUpdate', {
        used: parseInt(tokensUsed, 10),
        limit: parseInt(tokensLimit, 10),
        remaining: parseInt(tokensRemaining || '0', 10),
      })
    }

    // Check for grace period headers
    const gracePeriod = response.headers['x-grace-period']
    if (gracePeriod === 'true') {
      const gracePeriodEnds = response.headers['x-grace-period-ends'] || ''
      const graceDaysRemaining = parseInt(
        response.headers['x-grace-days-remaining'] || '0', 10
      )
      subscriptionEvents.emit('gracePeriod', {
        ends: gracePeriodEnds,
        daysRemaining: graceDaysRemaining,
      })
    }

    return response
  },
  async (error: AxiosError) => {
    // Track 401-storms — when many consecutive auth failures hit, the
    // user's session is dead and the dashboard is silently falling back
    // to [SIMULATED] / (Cached) data. Surface this via a global event
    // so the header / banner can show a "Session expired — re-login"
    // message instead of leaving the user staring at fake data.
    if (error.response?.status === 401) {
      console.warn('API returned 401 Unauthorized')
      try {
        const w = window as unknown as { _auth401Count?: number; _auth401Timer?: number }
        w._auth401Count = (w._auth401Count || 0) + 1
        if (w._auth401Timer) clearTimeout(w._auth401Timer)
        w._auth401Timer = window.setTimeout(() => { w._auth401Count = 0 }, 30000) as unknown as number
        if (w._auth401Count >= 3) {
          // Emit a session-expired event the header/banner subscribes to.
          window.dispatchEvent(new CustomEvent('ryedore:session-expired', {
            detail: { reason: 'multiple 401 responses', endpoint: error.config?.url },
          }))
          w._auth401Count = 0  // reset so we don't spam the listener
        }
      } catch { /* SSR safety */ }
    }

    // Handle 429 — distinguish rate-limit (transient) from token-limit
    // (subscription quota). Rate-limit gets a single transparent retry
    // with backoff so a burst of dashboard load fetches doesn't crash
    // the page. (May 4 2026 fix: Command Center lit up red OFFLINE on
    // 429 spikes from its own concurrent endpoint fan-out.)
    if (error.response?.status === 429) {
      const data = (error.response.data || {}) as Record<string, unknown>
      const isTokenLimit = typeof data.error === 'string' && (data.error as string).includes('token')
      if (isTokenLimit) {
        console.warn('API token limit exceeded')
        subscriptionEvents.emit('tokenLimitExceeded', error.response.data)
      } else {
        // Transient rate limit — retry once after Retry-After (or 1s)
        const cfg = error.config as (typeof error.config & { _retried429?: boolean })
        if (cfg && !cfg._retried429) {
          cfg._retried429 = true
          const retryAfter = parseInt(String(error.response.headers?.['retry-after'] ?? '1'), 10)
          const waitMs = Math.min(Math.max(retryAfter, 1) * 1000, 5000)
          await new Promise(resolve => setTimeout(resolve, waitMs))
          try {
            return await apiClient.request(cfg)
          } catch (retryErr) {
            // Fall through to original reject below — the second 429 is
            // genuine; let the page render its degraded state.
            return Promise.reject(retryErr)
          }
        }
      }
    }

    // Handle 403 (subscription expired or Spoke restriction)
    if (error.response?.status === 403) {
      const data = error.response.data as Record<string, unknown>
      if (data?.grace_period_ended) {
        console.warn('Subscription expired — past grace period')
        subscriptionEvents.emit('subscriptionExpired', data)
      } else if (typeof data?.detail === 'string' && (data.detail as string).includes('Spoke')) {
        console.warn(`Spoke restriction: ${data.detail}`)
        subscriptionEvents.emit('spokeRestriction', {
          endpoint: error.config?.url,
          message: data.detail,
        })
      }
    }

    return Promise.reject(error)
  }
)

// =============================================================================
// GENERIC API METHODS
// =============================================================================

export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.get<T>(url, config)
  return response.data
}

export async function post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.post<T>(url, data, config)
  return response.data
}

export async function put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.put<T>(url, data, config)
  return response.data
}

export async function patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.patch<T>(url, data, config)
  return response.data
}

export async function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.delete<T>(url, config)
  return response.data
}

// Error helper
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.error?.message || error.message
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An unknown error occurred'
}

export default apiClient
